import { useNavigate } from 'react-router-dom';
import { ClipboardList, FileCheck2, PackageCheck, Plane, ShoppingCart } from 'lucide-react';
import { ARTICLE_CHANGE_KIND_LABELS } from '@proerp/shared';
import { Badge } from '@proerp/ui';
import { formatDate } from '@/lib/format';
import { useArticleNumberRequests } from '@/features/engineering/articleNumberRequestsApi';
import { useArticleChangeRequests } from '@/features/inventory/articleChangeRequestsApi';
import { useAllLeaveRequests } from '@/features/leave/leaveApi';
import { usePickingOrders } from '@/features/commissioning/commissioningApi';
import { usePurchaseOrders } from '@/features/procurement/procurementApi';
import { useAllTrips } from '@/features/travel/travelApi';
import { TaskCard, TaskRow } from './TaskCard';

/**
 * Freigabe-/Genehmigungs-Queues fürs Dashboard (ADR 0062). Jedes Widget ist in der
 * Registry an die passende Berechtigung gebunden und wird nur dann gerendert (und
 * feuert erst dann seine Abfrage) — so entstehen für Unberechtigte keine 403-Requests.
 */

/** Kommissionieraufträge, die auf Freigabe warten (Status ENTWURF). commissioning.release */
export function PickingReleaseWidget() {
  const navigate = useNavigate();
  const query = usePickingOrders();
  const draft = (query.data?.data ?? []).filter((order) => order.status === 'ENTWURF');
  return (
    <TaskCard
      title="Kommissionierung: Freigabe"
      icon={PackageCheck}
      isLoading={query.isLoading}
      isEmpty={draft.length === 0}
      emptyText="Keine Kommissionieraufträge zur Freigabe."
    >
      {draft.slice(0, 6).map((order) => (
        <TaskRow
          key={order.id}
          onClick={() => navigate(`/kommissionierung/${order.id}`)}
          left={
            <>
              <span className="font-mono text-xs text-muted-foreground">{order.number}</span>
              <span className="ml-2 text-foreground">{order.projectName}</span>
            </>
          }
          right={<Badge tone="warning">Entwurf</Badge>}
        />
      ))}
    </TaskCard>
  );
}

/** Bestellungen im Pool, die auf Freigabe warten. procurement.update */
export function PoolReleaseWidget() {
  const navigate = useNavigate();
  const query = usePurchaseOrders(undefined, undefined, { pool: true });
  const pool = query.data?.data ?? [];
  return (
    <TaskCard
      title="Bestellpool: Freigabe"
      icon={ShoppingCart}
      isLoading={query.isLoading}
      isEmpty={pool.length === 0}
      emptyText="Keine Bestellungen im Pool."
    >
      {pool.slice(0, 6).map((order) => (
        <TaskRow
          key={order.id}
          onClick={() => navigate(`/beschaffung/${order.id}`)}
          left={
            <>
              <span className="font-mono text-xs text-muted-foreground">{order.number}</span>
              <span className="ml-2 text-foreground">{order.supplierName}</span>
            </>
          }
          right={order.projectCode ? <Badge tone="neutral">{order.projectCode}</Badge> : null}
        />
      ))}
    </TaskCard>
  );
}

/**
 * Offene Artikel-Anträge zur Freigabe. engineering.approve
 *
 * Zählt und listet BEIDE Antragsarten der Warteschlange — Artikelnummern
 * (ADR 0031) und Änderungen am Artikelstamm (ADR 0089/VB-26016). Sonst zeigte
 * das Dashboard weniger, als das Abzeichen in der Navigation zählt, und die
 * Änderungsanträge blieben unbemerkt liegen. Beide Listen kommen absteigend
 * nach Datum; die Zusammenführung behält diese Reihenfolge bei.
 */
export function ArticleRequestWidget() {
  const navigate = useNavigate();
  const numberQuery = useArticleNumberRequests({ status: 'PENDING' });
  const changeQuery = useArticleChangeRequests({ status: 'PENDING' });
  const pending = [
    ...(numberQuery.data?.data ?? []).map((request) => ({
      id: request.id,
      createdAt: request.createdAt,
      code: request.drawingNumber ?? '—',
      text: request.materialCode,
      kind: 'Neue Nummer',
    })),
    ...(changeQuery.data?.data ?? []).map((request) => ({
      id: request.id,
      createdAt: request.createdAt,
      code: request.articleNumber,
      text: request.articleName,
      kind: ARTICLE_CHANGE_KIND_LABELS[request.kind],
    })),
  ].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return (
    <TaskCard
      title="Artikel-Anträge: Freigabe"
      icon={FileCheck2}
      isLoading={numberQuery.isLoading || changeQuery.isLoading}
      isEmpty={pending.length === 0}
      emptyText="Keine offenen Artikel-Anträge."
    >
      {pending.slice(0, 6).map((request) => (
        <TaskRow
          key={request.id}
          onClick={() => navigate('/engineering/antraege')}
          left={
            <>
              <span className="font-mono text-xs text-muted-foreground">{request.code}</span>
              <span className="ml-2 text-foreground">{request.text}</span>
            </>
          }
          right={<Badge tone="warning">{request.kind}</Badge>}
        />
      ))}
    </TaskCard>
  );
}

/** Urlaubs-/Fehlzeitanträge zur Genehmigung. leave.manage */
export function LeaveApprovalWidget() {
  const navigate = useNavigate();
  const query = useAllLeaveRequests({ status: 'PENDING' });
  const pending = query.data?.data ?? [];
  return (
    <TaskCard
      title="Urlaub: Genehmigung"
      icon={ClipboardList}
      isLoading={query.isLoading}
      isEmpty={pending.length === 0}
      emptyText="Keine offenen Urlaubsanträge."
    >
      {pending.slice(0, 6).map((request) => (
        <TaskRow
          key={request.id}
          onClick={() => navigate('/urlaubskalender')}
          left={
            <>
              <span className="text-foreground">{request.employeeName}</span>
              <span className="ml-2 text-xs text-muted-foreground">{request.type}</span>
            </>
          }
          right={
            <Badge tone="neutral">
              {formatDate(request.startDate)}–{formatDate(request.endDate)}
            </Badge>
          }
        />
      ))}
    </TaskCard>
  );
}

/** Reisen zur Genehmigung. travel.manage */
export function TripApprovalWidget() {
  const navigate = useNavigate();
  const query = useAllTrips({ status: 'PENDING' });
  const pending = query.data?.data ?? [];
  return (
    <TaskCard
      title="Reisen: Genehmigung"
      icon={Plane}
      isLoading={query.isLoading}
      isEmpty={pending.length === 0}
      emptyText="Keine offenen Reiseanträge."
    >
      {pending.slice(0, 6).map((trip) => (
        <TaskRow
          key={trip.id}
          onClick={() => navigate('/reisekosten')}
          left={
            <>
              <span className="text-foreground">{trip.employeeName}</span>
              <span className="ml-2 text-xs text-muted-foreground">{trip.title}</span>
            </>
          }
          right={<Badge tone="neutral">{formatDate(trip.startDate)}</Badge>}
        />
      ))}
    </TaskCard>
  );
}
